import React, { useState, isValidElement, useEffect } from 'react';
import {
  Book,
  MessageSquare,
  Video,
  FileText,
  HelpCircle,
  Clock,
  Mail,
  LifeBuoy,
  ChevronDown,
  Search,
} from 'lucide-react';
import { Header, useLayout } from '@/components/layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/tooltip';

export function HelpPage() {
  const faqs = [
    {
      question: 'How do I start a video assessment?',
      answer: (
        <>
          Navigate to the <strong>Schedule</strong> tab, find your upcoming session, and click{' '}
          <strong>Join</strong>. Ensure your camera and microphone permissions are enabled in your
          browser.
        </>
      ),
    },
    {
      question: 'How long does the AI analysis take?',
      answer: (
        <>
          Typically, the AI performs continuous, real-time analysis throughout the video as the
          content is being processed.
        </>
      ),
    },
    {
      question: 'Can I upload my own video for analysis?',
      answer: (
        <>
          Yes, you can upload your own video. To do this, navigate to the <strong>Claim</strong>{' '}
          detail page by clicking the <strong>Manual Upload</strong> button, and then follow the
          prompts to upload your video for analysis.
        </>
      ),
    },
    {
      question: 'What video formats are supported for upload?',
      answer: (
        <>
          We support MP4, MOV, and AVI formats. Files should be under 500MB for optimal processing
          speed.
        </>
      ),
    },
    {
      question: 'Can I download my reports as a PDF?',
      answer: (
        <>
          Yes, you can download reports from the <strong>Claim</strong> detail page by clicking the{' '}
          download icon on the right side of the file.
        </>
      ),
    },
  ];

  const categories = [
    { name: 'Getting Started', icon: Book, count: 12 },
    { name: 'Video Assessments', icon: Video, count: 8 },
    { name: 'Claims Management', icon: FileText, count: 15 },
    { name: 'AI Insights', icon: LifeBuoy, count: 6 },
  ];

  const { isMobile } = useLayout();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);

  const toggleFAQ = (question: string) => {
    setExpandedQuestion(expandedQuestion === question ? null : question);
  };

  const extractText = (node: React.ReactNode): string => {
    if (!node) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(extractText).join('');
    if (isValidElement(node)) return extractText(node.props.children);
    return '';
  };

  const filteredFaqs = faqs.filter(faq => {
    const query = searchQuery.toLowerCase();
    const answerContent = extractText(faq.answer).toLowerCase();
    return faq.question.toLowerCase().includes(query) || answerContent.includes(query);
  });

  useEffect(() => {
    if (searchQuery.trim() !== '' && filteredFaqs.length > 0) {
      setExpandedQuestion(filteredFaqs[0].question);
    }
  }, [searchQuery, filteredFaqs.length]);

  return (
    <div className="flex flex-col h-full bg-slate-50/50 dark:bg-slate-950/50">
      <Header
        title="Help"
        description="Find answers, explore tutorials, or contact our support team"
      >
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder={isMobile ? 'Search' : 'Search for help articles...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={isMobile ? 'w-[120px]' : 'w-[280px]'}
          />
        </div>
      </Header>

      <div className="flex-1 overflow-auto p-6">
        <div className="mx-auto space-y-8">
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* FAQs */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold flex items-center gap-2 pb-3">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {filteredFaqs.map(faq => {
                    const isExpanded = expandedQuestion === faq.question;
                    return (
                      <Card
                        key={faq.question}
                        className="border-border/60 shadow-sm overflow-hidden"
                      >
                        <CardHeader
                          className="py-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                          onClick={() => toggleFAQ(faq.question)}
                        >
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base sm:text-lg font-semibold">
                              {faq.question}
                            </CardTitle>
                            <ChevronDown
                              className={cn(
                                'h-5 w-5 text-muted-foreground transition-transform duration-200',
                                isExpanded && 'rotate-180'
                              )}
                            />
                          </div>
                        </CardHeader>
                        <div
                          className={cn(
                            'grid transition-all duration-200 ease-in-out',
                            isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                          )}
                        >
                          <div className="overflow-hidden">
                            <CardContent className="pb-4 pt-0">
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                {faq.answer}
                              </p>
                            </CardContent>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                  {filteredFaqs.length === 0 && (
                    <div className="bg-card rounded-xl border shadow-sm p-12 text-center">
                      <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No help articles found
                      </h3>
                      <p className="text-muted-foreground">
                        Try searching with different keywords.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Contact Support */}
            <div className="space-y-6">
              <Card className="border-primary/20 bg-primary/5 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    Live Support
                  </CardTitle>
                  <CardDescription>Need immediate assistance?</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>
                      Average wait time: <b>&lt; 2 minutes</b>
                    </span>
                  </div>
                  <InfoTooltip
                    content="Coming Soon"
                    direction="top"
                    fontSize="text-[11px]"
                    className="w-full"
                    trigger={
                      <Button className="w-full shadow-lg shadow-primary/20" disabled>
                        Start Chat
                      </Button>
                    }
                  />
                </CardContent>
              </Card>

              <Card className="border-border/60 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Contact Us</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-sm">
                      <p className="font-medium">Email Support</p>
                      <p className="text-xs text-muted-foreground">support@tci.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-sm">
                      <p className="font-medium">Support Hours</p>
                      <p className="text-xs text-muted-foreground">Mon-Fri, 8am - 5pm GMT</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
